import React, { useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  Heading,
  Icon,
} from '@chakra-ui/react';
import { FaFileContract, FaUserShield } from 'react-icons/fa';

/**
 * LegalModal — the single door to the full legal documents. Shown from the
 * footer Terms button AND the onboarding gate's terms link (owner spec 9-5):
 * a modal with one button per document, each opening the corresponding FULL
 * legal doc (the same versions the latest Burrow Lite shipped) in a new tab.
 *
 * Implemented as a custom fixed panel (NOT Chakra Modal) so its stacking can
 * sit ABOVE the onboarding gate overlay (z 10001) reliably — Chakra's Modal
 * portal is capped at z 1400 and would hide underneath the gate.
 *
 * Docs (served by this app from public/legal/):
 *   - Terms and Conditions -> BurrowTv-Terms-of-Service.html
 *   - Privacy Agreement    -> BurrowTv-Privacy-Policy.html
 */
const DOCS = [
  { label: 'Terms and Conditions', file: '/legal/BurrowTv-Terms-of-Service.html', icon: FaFileContract, desc: 'The full Private Membership Agreement' },
  { label: 'Privacy Agreement', file: '/legal/BurrowTv-Privacy-Policy.html', icon: FaUserShield, desc: 'How we collect and handle data' },
];

const LegalModal = ({ isOpen, onClose }) => {
  // Close on Escape, for good keyboard UX
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const openDoc = (file) => {
    window.open(file, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label="Legal Documents"
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={12000}
      bg="blackAlpha.800"
      backdropFilter="blur(3px)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={onClose}
    >
      <Box
        bg="gray.800"
        color="white"
        maxW="440px"
        w="90%"
        borderRadius="xl"
        border="1px"
        borderColor="whiteAlpha.200"
        boxShadow="2xl"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <FlexHeader onClose={onClose} />
        <Box px={6} py={6}>
          <Text color="gray.400" fontSize="sm" mb={5}>
            Select a document to read the full agreement in a new tab.
          </Text>
          <VStack spacing={4} align="stretch">
            {DOCS.map(({ label, file, icon, desc }) => (
              <Button
                key={file}
                size="lg"
                justifyContent="flex-start"
                leftIcon={<Icon as={icon} boxSize={5} />}
                bg="whiteAlpha.100"
                color="white"
                border="1px"
                borderColor="whiteAlpha.300"
                _hover={{ bg: 'yellow.600', color: 'black', borderColor: 'yellow.600' }}
                onClick={() => openDoc(file)}
              >
                <VStack align="flex-start" spacing={0}>
                  <Text fontWeight="bold">{label}</Text>
                  <Text fontSize="xs" fontWeight="normal" opacity={0.75}>{desc}</Text>
                </VStack>
              </Button>
            ))}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

const FlexHeader = ({ onClose }) => (
  <Box
    px={6}
    py={4}
    borderBottomWidth="1px"
    borderColor="gray.700"
    display="flex"
    alignItems="center"
    justifyContent="space-between"
  >
    <Heading size="md" color="white">Legal Documents</Heading>
    <Button
      size="sm"
      variant="ghost"
      color="whiteAlpha.700"
      aria-label="Close"
      onClick={onClose}
      _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
    >
      ✕
    </Button>
  </Box>
);

export default LegalModal;
